import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Button from '../common/Button/Button';
import LoadingSpinner from '../common/LoadingSpinner/LoadingSpinner';
import Modal from '../common/Modal/Modal';
import i18n from '../../i18n';
import {
    deleteMultipleIncomeUnitsByIds,
    getFullTenantSummaryBySnapshotId,
    resetGetFullTenantSummaryBySnapshotId,
    upsertBatchIncomeUnits
} from '../../redux/actions/incomeUnit/incomeUnit';
import { GetFullTenantSummaryState } from '../../redux/reducers/getIncomeUnit/getFullTenantSummaryReducer';
import { GetElsLookupsState } from '../../redux/reducers/getLookups/getElsLookupsReducer';
import { SaveResultState } from '../../redux/reducers/getSaveData/saveResultReducer';
import {
    selectDeleteIncomeUnit,
    selectELSLookups,
    selectGetFullTenantSummary,
    selectSaveResult
} from '../../redux/selectors/exportSelectorPaths';
import BasicUnitInfoWizard from './BasicUnitInfoWizard';
import ConfirmImportWizard from './ConfirmImportWizard';
import FixEndDateWizard from './FixEndDateWizard';
import FloorWizard from './FloorWizard';
import FreeMonthWizard from './FreeMonthWizard';
import LandLeaseWizard from './LandLeaseWizard';
import LeaseRateWizard from './LeaseRateWizard';
import LeaseStatusWizard from './LeaseStatusWizard';
import LeaseTermWizard from './LeaseTermWizard';
import LeaseTypeWizard from './LeaseTypeWizard';
import NotExpiredLeaseWizard from './NotExpiredLeaseWizard';
import OperatingExpensesWizard from './OperatingExpensesWizard';
import StepUpDetailsWizard from './StepUpDetailsWizard';
import StepUpWizard from './StepUpWizard';
import SummaryWizard from './SummaryWizard';
import UnitUseWizard from './UnitUseWizard';
import UpdateImportWizard from './UpdateImportWizard';
import VacancyStartWizard from './VacancyStartWizard';
import { WizardExitTrigger, WizardInteractionEventPayload } from '../../types/GA4Events/WizardEventParams';
import { getDefaultUnit } from '../../types/unit/IncomeUnit';
import trackWizardEvent, { createWizardEventPayload } from '../../utils/GA4EventHelpers/trackWizardEvent';
import "./GuidedExperienceWizard.css";
import { BaseAsyncReducerState } from '../../redux/types';
import { setSaveResultSuccess } from '../../redux/actions/saveData/saveData';

interface GuidedExperienceWizardProps {
    snapshotId: number;
    onCancel: (active_step: number, exit_method: WizardExitTrigger, submitted: boolean) => void;
    sendActiveStepToParent: (step: number) => void;
}

export default function GuidedExperienceWizard(props: GuidedExperienceWizardProps): JSX.Element {
    const { t } = useTranslation("guided_wizard");
    const dispatch = useDispatch();
    const elsLookupsState: GetElsLookupsState = useSelector(selectELSLookups);
    const getFullTenantSummaryState: GetFullTenantSummaryState = useSelector(selectGetFullTenantSummary);
    const saveResultState: SaveResultState = useSelector(selectSaveResult)
    const deleteIncomeUnitState: BaseAsyncReducerState = useSelector(selectDeleteIncomeUnit)
    const [submitted, setSubmitted] = useState(false);
    const [tenantList, setTenantList] = useState<Record<string, unknown>[]>([]);
    const [confirmedImport, setConfirmedImport] = useState<undefined | boolean>(undefined);
    const tenantSummaryType = "1"; // PIER2-1317: When UI sends tenantSummaryType = 1 to the backend, both unit type 1 and 2 on the snapshot are returned from backend
    const [deletedUnits, setDeletedUnits] = useState<number[]>([])
    const isInternalUser = sessionStorage.getItem("isInternalUser") ? true : false
    const [selectDelete, setSelectDelete] = useState(false)

    // This tracks the number of units on each floor level
    const [wizardNumber, setWizardNumber] = useState((elsLookupsState.dropdowns.floorLevelOptions as Record<string, unknown>[])
        .reduce((wizardNumberRecord, floorLevelOption) => ({
            [floorLevelOption.value as string]:
                (tenantList.filter((tenant) => tenant["floorLevel"] === floorLevelOption.value)).length,
            ...wizardNumberRecord
        }), {}));

    // This is the wizard number before the user changes the number of units on each floor level
    const oldWizardNumber = (elsLookupsState.dropdowns.floorLevelOptions as Record<string, unknown>[])
        .reduce((wizardNumberRecord, floorLevelOption) => ({
            [floorLevelOption.value as string]:
                (tenantList.filter((tenant) => tenant["floorLevel"] === floorLevelOption.value)).length,
            ...wizardNumberRecord
        }), {});

    const simplifiedFloorNameLookup = ((elsLookupsState.dropdowns.floorLevelOptions as Record<string, unknown>[])
        .reduce((simplifiedLookup, floorLevelOption) => ({ [floorLevelOption.value as string]: i18n.language === "fr" ? floorLevelOption.frenchText : floorLevelOption.text, ...simplifiedLookup }), { "undefined": "" }));

    const simplifiedLeaseStatusLookup = ((elsLookupsState.dropdowns.leaseStatusCodeOptions as Record<string, unknown>[]).reduce((simplifiedLookup, leaseStatus) =>
        ({ [leaseStatus["value"] as string]: leaseStatus["text"], ...simplifiedLookup }), { "undefined": "" }));

    const simplifiedLeaseTypeLookup = ((elsLookupsState.dropdowns.leaseTypeCodeOptions as Record<string, unknown>[]).reduce((simplifiedLookup, leaseType) =>
        ({ [leaseType["value"] as string]: leaseType["text"], ...simplifiedLookup }), { "undefined": "" }));

    const simplifiedUnitSubtypeLookup = ((elsLookupsState.dropdowns.unitSubType1 as Record<string, unknown>[]).reduce((simplifiedLookup, unitSubtype) =>
        ({ [unitSubtype["unitSubtypeCode"] as string]: unitSubtype["unitSubtypeDescription"], ...simplifiedLookup }), { "undefined": "" }));

    const [totalPages, setTotalPages] = useState<number | undefined>(undefined);
    const [currentPage, setCurrentPage] = useState(1);
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [warningMessage, setWarningMessage] = useState("");
    const [proceed, setProceed] = useState(false);
    const [wizardUpdated, setWizardUpdated] = useState(false);
    const [closingWizard, setClosingWizard] = useState(false);
    const [unitUseForAll, setUnitUseForAll] = useState("");
    const [isSameType, setIsSameType] = useState<boolean | undefined>(undefined);
    const [leaseTypeForAll, setLeaseTypeForAll] = useState("");
    const [leaseTypeSameType, setLeaseTypeSameType] = useState<boolean | undefined>(undefined);
    const [hasFreeMonth, setHasFreeMonth] = useState<boolean | undefined>(undefined);
    const [hasLandLease, setHasLandLease] = useState<boolean | undefined>(undefined);
    const [hasStepUp, setHasStepUp] = useState<boolean | undefined>(undefined);
    const [stepUpArr, setStepUpArr] = useState<number[]>([]);
    const [assignedPreviousUnits, setAssignedPreviousUnits] = useState(false);
    const [confirmedArr, setConfirmedArr] = useState<number[]>([]);
    const [confirmedTenants, setConfirmedTenants] = useState<Record<string, unknown>[]>([]);
    const [confirmedArrSeparated, setConfirmedArrSeparated] = useState(false);
    const [fetchedFullUnits, setFetchedFullUnits] = useState(false);
    const [fetchFullUnitsDataHasRun, setFetchFullUnitsDataHasRun] = useState(false);
    const removedTenantIds = useRef<number[]>([]);

    const updateWizardNumber = (value: number | null, name: string) => {
        if (value === null) {
            value = 0
        }

        name = name.substring(13);

        const newWizardNumber = {
            ...wizardNumber,
            [name]: value
        }
        setWizardNumber(newWizardNumber);
        setWizardUpdated(true);
    }

    const updateTenantListInfo = (e: React.ChangeEvent<Element> | string | undefined | number | null, index: number, fieldName: string, isNumber = false, isCheckbox = false) => {
        const newTList = [...tenantList];
        let value;
        if (e && isCheckbox) {
            value = ((e as React.ChangeEvent<Element>).target as HTMLInputElement).checked;
        } else if (typeof e === "string" || typeof e === "number") {
            value = e;
        } else if (e === undefined || e === null) {
            value = undefined;
        } else {
            value = (e.target as HTMLInputElement).value;
        }
        if (fieldName === "leaseStatusCode" && value === "1") {
            newTList[index]["tenantName"] = "Vacant";
            newTList[index]["analysisFlag"] = "N";
            newTList[index]["analysisReviewedBy"] = localStorage.getItem("username") ?? "SYSTEM";
            newTList[index]["analysisReviewedDate"] = new Date().toISOString();
        }
        if (fieldName === "leaseTypeCode" && (value === "1" || (newTList[index]["leaseTypeCode"] === "2" && (value === "3" || value === "4")))) {
            newTList[index]["includeInsuranceFlag"] = undefined;
            newTList[index]["includeManagementFlag"] = undefined;
            newTList[index]["includeMaintenanceFlag"] = undefined;
            newTList[index]["includeHydroFlag"] = undefined;
            newTList[index]["includeWaterFlag"] = undefined;
            newTList[index]["includeHvacFlag"] = undefined;
            newTList[index]["includePropertyTaxFlag"] = undefined;
        } else if (fieldName === "leaseTypeCode" && value === "2") {
            newTList[index]["includeInsuranceFlag"] = true;
            newTList[index]["includeManagementFlag"] = true;
            newTList[index]["includeMaintenanceFlag"] = true;
            newTList[index]["includeHydroFlag"] = true;
            newTList[index]["includeWaterFlag"] = true;
            newTList[index]["includeHvacFlag"] = true;
            newTList[index]["includePropertyTaxFlag"] = true;
        }
        if (isNumber) {
            if (value === undefined) {
                newTList[index][fieldName] = value;
            } else {
                newTList[index][fieldName] = parseFloat(value as string);
            }
        } else {
            newTList[index][fieldName] = value;
        }
        setTenantList(newTList);
    }

    const removeUnit = (index: number) => {
        const newTList = [...tenantList];
        const removedTenant: Record<string, unknown>[] = newTList.splice(index, 1);
        removedTenantIds.current.push(removedTenant[0]["tenantId"] as number);
        let totalPages = 1;
        if (newTList.length > 10) {
            totalPages = Math.ceil(newTList.length / 10);
            setTotalPages(totalPages);
        } else {
            setTotalPages(undefined);
        }
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
        setTenantList(newTList);
    }

    const previousWizardPage = () => {
        const pageElements = (confirmedImport === true ? importWizardElements : orderedWizardElements)
        if (currentWizardPage > 0) {
            if ((pageElements[currentWizardPage - 1].key === "wizard-vacancy-start" && tenantList.filter((tenant) => tenant["leaseStatusCode"] === "1").length === 0)
                || (pageElements[currentWizardPage - 1].key === "wizard-step-up-details" && stepUpArr.length === 0)) {
                setCurrentWizardPage(currentWizardPage - 2);
            } else {
                setCurrentWizardPage(currentWizardPage - 1);
            }

        }
    }

    const nextWizardPage = () => {
        const pageElements = (confirmedImport === true ? importWizardElements : orderedWizardElements)
        if (currentWizardPage < ((pageElements.length) - 1)) {
            // Proceed only if number of units are added, otherwise return toast error
            if (confirmedImport === false && Object.values(wizardNumber).every(floor => floor === 0)) {
                toast.error(t("floor-wizard.num-units-required") as string)
                return;
            }
            if (wizardUpdated) {
                const keys = Object.keys(wizardNumber);
                let warningFloors = "";
                let totalUnits = 0;

                keys.forEach((key) => {
                    if ((wizardNumber[key] as number) < (oldWizardNumber[key] as number)) {
                        warningFloors += (warningFloors === "" ? "" : " ,") + simplifiedFloorNameLookup[key];
                    }
                    totalUnits += (wizardNumber[key] as number);
                });


                if (totalUnits === 0) {
                    return;
                }
                if (warningFloors !== "") {
                    setWarningMessage(warningFloors + t("floor-wizard.floor-warning"));
                    setShowWarningModal(true);
                    return;
                } else {
                    setProceed(true);
                }
            }
            if (tenantList.length > 0) {
                setCurrentPage(1);
                if (pageElements[currentWizardPage].key === "wizard-not-expired-lease") {
                    setWarningMessage(t("not-expired-lease-wizard.not-expired-lease-warning"));
                    setShowWarningModal(true);
                    return;
                }
                if ((pageElements[currentWizardPage + 1].key === "wizard-vacancy-start" && tenantList.filter((tenant) => tenant["leaseStatusCode"] === "1").length === 0)
                    || (pageElements[currentWizardPage + 1].key === "wizard-step-up-details" && stepUpArr.length === 0)
                    || (pageElements[currentWizardPage + 1].key === "wizard-not-expired-lease" && tenantList.filter((tenant) => ((tenant["endDate"] && new Date(tenant["endDate"] as string) >= new Date()) || tenant["leaseStatusCode"] === "1" || tenant["leaseStatusCode"] === "5")).length === 0)) {
                    setCurrentWizardPage(currentWizardPage + 2);
                } else {
                    setCurrentWizardPage(currentWizardPage + 1);
                }
            } else if (pageElements[currentWizardPage].key === "wizard-update-import") {
                setCurrentWizardPage(currentWizardPage + 12);
            }
        }
    }
    const commonWizardElements = [
        <LeaseStatusWizard key="wizard-lease-status" updateWizardFunction={updateTenantListInfo} tenantList={tenantList} floorDescriptionLookup={simplifiedFloorNameLookup}
            totalPages={totalPages} currentPage={currentPage} changePage={setCurrentPage} leaseStatusLookup={elsLookupsState.dropdowns.leaseStatusCodeOptions as Record<string, unknown>[]} />,
        <LandLeaseWizard key="wizard-land-lease" updateWizardFunction={updateTenantListInfo} tenantList={tenantList} floorDescriptionLookup={simplifiedFloorNameLookup}
            hasLandLease={hasLandLease} setHasLandLease={setHasLandLease} totalPages={totalPages} currentPage={currentPage} changePage={setCurrentPage} />,
        <LeaseTermWizard key="wizard-lease-term" updateWizardFunction={updateTenantListInfo} tenantList={tenantList} floorDescriptionLookup={simplifiedFloorNameLookup}
            totalPages={totalPages} currentPage={currentPage} changePage={setCurrentPage} leaseStatusLookup={simplifiedLeaseStatusLookup} />,
        <VacancyStartWizard key="wizard-vacancy-start" updateWizardFunction={updateTenantListInfo} tenantList={tenantList} floorDescriptionLookup={simplifiedFloorNameLookup} leaseStatusLookup={simplifiedLeaseStatusLookup} />,
        <UnitUseWizard key="wizard-unit-use" updateWizardFunction={updateTenantListInfo} tenantList={tenantList} floorDescriptionLookup={simplifiedFloorNameLookup} unitUseForAll={unitUseForAll}
            setUnitUseForAll={setUnitUseForAll} isSameType={isSameType} setIsSameType={setIsSameType} totalPages={totalPages} currentPage={currentPage}
            changePage={setCurrentPage} unitUseOptions={elsLookupsState.dropdowns.unitSubType1 as Record<string, unknown>[]} />,
        <FreeMonthWizard key="wizard-free-month" updateWizardFunction={updateTenantListInfo} tenantList={tenantList} floorDescriptionLookup={simplifiedFloorNameLookup}
            hasFreeMonth={hasFreeMonth} setHasFreeMont={setHasFreeMonth} totalPages={totalPages} currentPage={currentPage} changePage={setCurrentPage} />,
        <LeaseTypeWizard key="wizard-lease-type" updateWizardFunction={updateTenantListInfo} tenantList={tenantList} floorDescriptionLookup={simplifiedFloorNameLookup} leaseTypeForAll={leaseTypeForAll}
            setLeaseTypeForAll={setLeaseTypeForAll} isSameType={leaseTypeSameType} setIsSameType={setLeaseTypeSameType} totalPages={totalPages} currentPage={currentPage}
            changePage={setCurrentPage} leaseTypeOptions={elsLookupsState.dropdowns.leaseTypeCodeOptions as Record<string, unknown>[]} unitSubtypeLookup={simplifiedUnitSubtypeLookup} />,
        <LeaseRateWizard key="wizard-lease-rate" updateWizardFunction={updateTenantListInfo} tenantList={tenantList} floorDescriptionLookup={simplifiedFloorNameLookup}
            totalPages={totalPages} currentPage={currentPage} changePage={setCurrentPage} unitSubtypeLookup={simplifiedUnitSubtypeLookup} />,
        <OperatingExpensesWizard key="wizard-operating-expenses" updateWizardFunction={updateTenantListInfo} tenantList={tenantList} floorDescriptionLookup={simplifiedFloorNameLookup}
            totalPages={totalPages} currentPage={currentPage} changePage={setCurrentPage} leaseTypeLookup={simplifiedLeaseTypeLookup} />,
        <StepUpWizard key="wizard-step-up" updateWizardFunction={updateTenantListInfo} tenantList={tenantList} floorDescriptionLookup={simplifiedFloorNameLookup}
            hasStepUp={hasStepUp} setHasStepUp={setHasStepUp} stepUpArr={stepUpArr} setStepUpArr={setStepUpArr} totalPages={totalPages} currentPage={currentPage} changePage={setCurrentPage} />,
        <StepUpDetailsWizard key="wizard-step-up-details" updateWizardFunction={updateTenantListInfo} tenantList={tenantList} floorDescriptionLookup={simplifiedFloorNameLookup} stepUpArray={stepUpArr} />,
        <SummaryWizard key="wizard-summary" tenantList={tenantList} setTenantList={setTenantList} stepUpArr={stepUpArr} setStepUpArr={setStepUpArr} confirmedTenants={confirmedTenants} setConifirmedList={setConfirmedTenants} floorDescriptionLookup={simplifiedFloorNameLookup} leaseStatusLookup={simplifiedLeaseStatusLookup} leaseTypeLookup={simplifiedLeaseTypeLookup} />
    ];
    const orderedWizardElements = [
        <FloorWizard key="wizard-floor" updateWizardFunction={updateWizardNumber} wizardNumber={wizardNumber} />,
        <BasicUnitInfoWizard key="wizard-basic-unit-info" updateWizardFunction={updateTenantListInfo} tenantList={tenantList} floorDescriptionLookup={simplifiedFloorNameLookup}
            totalPages={totalPages} currentPage={currentPage} changePage={setCurrentPage} />,
        ...commonWizardElements
    ];
    const importWizardElements = [
        <FixEndDateWizard key="wizard-fix-end-date" updateWizardFunction={updateTenantListInfo} tenantList={tenantList} floorDescriptionLookup={simplifiedFloorNameLookup} leaseStatusLookup={simplifiedLeaseStatusLookup}
            leaseTypeLookup={simplifiedLeaseTypeLookup} />,
        <NotExpiredLeaseWizard key="wizard-not-expired-lease" tenantList={tenantList} floorDescriptionLookup={simplifiedFloorNameLookup} leaseStatusLookup={simplifiedLeaseStatusLookup}
            leaseTypeLookup={simplifiedLeaseTypeLookup} confirmedArr={confirmedArr} setConfirmedArr={setConfirmedArr} />,
        <UpdateImportWizard key="wizard-update-import" updateWizardFunction={updateTenantListInfo} removeUnit={removeUnit} tenantList={tenantList} floorDescriptionLookup={simplifiedFloorNameLookup}
            leaseStatusLookup={simplifiedLeaseStatusLookup} confirmedArr={confirmedArr} setTenantList={setTenantList} setConfirmedTenants={setConfirmedTenants} setTotalPages={setTotalPages}
            totalPages={totalPages} currentPage={currentPage} changePage={setCurrentPage} setStepUpArr={setStepUpArr} confirmedArrSeparated={confirmedArrSeparated} setConfirmedArrSeparated={setConfirmedArrSeparated} />,
        ...commonWizardElements
    ]
    const [currentWizardPage, setCurrentWizardPage] = useState(0);

    useEffect(() => {
        const currentRollNumber = sessionStorage.getItem("rollNumber")
        if (currentRollNumber) {
            const commericalWizardRolls = sessionStorage.getItem("commericalWizard")
            if (commericalWizardRolls) {
                const rolls = commericalWizardRolls.split(",")
                if (!rolls.includes(currentRollNumber)) {
                    sessionStorage.setItem("commericalWizard", commericalWizardRolls + "," + currentRollNumber)
                    const wizardEventPayload: WizardInteractionEventPayload = createWizardEventPayload({
                        wizard_type: "commercial",
                        wizard_interaction_type: "enter",
                        wizard_entry_trigger: "once_per_session"
                    })
                    trackWizardEvent(wizardEventPayload)
                }
            } else {
                sessionStorage.setItem("commericalWizard", currentRollNumber)
                const wizardEventPayload: WizardInteractionEventPayload = createWizardEventPayload({
                    wizard_type: "commercial",
                    wizard_interaction_type: "enter",
                    wizard_entry_trigger: "once_per_session"
                })
                trackWizardEvent(wizardEventPayload)
            }
        }
    }, [])

    // this hook sends active wizard step to parent component for GA tracking
    useEffect(() => {
        props.sendActiveStepToParent(currentWizardPage + 1);
    }, [currentWizardPage, props]);

    useEffect(() => {
        if (submitted && saveResultState.loadingCount >= 0 && saveResultState.status) {
            setSubmitted(false);
            if (saveResultState.status === 200) {
                props.onCancel((currentWizardPage + 1), "system_error", submitted);
            } else if (saveResultState.message) {
                toast.error(saveResultState.message)
            }
        }
    }, [currentWizardPage, props, saveResultState, submitted]);

    useEffect(() => {
        if (!fetchedFullUnits && !getFullTenantSummaryState.loading) {
            dispatch(resetGetFullTenantSummaryBySnapshotId());
            dispatch(getFullTenantSummaryBySnapshotId(props.snapshotId as number, tenantSummaryType))
            setFetchedFullUnits(true);
        }
    }, [dispatch, fetchedFullUnits, getFullTenantSummaryState.loading, props.snapshotId]);

    useEffect(() => {
        if (getFullTenantSummaryState.loading && !fetchFullUnitsDataHasRun) {
            setFetchFullUnitsDataHasRun(true)
        }
    }, [getFullTenantSummaryState.loading, fetchFullUnitsDataHasRun])

    useEffect(() => {
        if (fetchedFullUnits && !getFullTenantSummaryState.loading && fetchFullUnitsDataHasRun) {
            if (getFullTenantSummaryState.success) {
                const summary = getFullTenantSummaryState.tenantSummary
                if (!summary || summary.length === 0) {
                    setConfirmedImport(false)
                } else {
                    if (!assignedPreviousUnits && confirmedImport) {
                        const tenantSummary = (summary as unknown as Record<string, unknown>[]).map((tenant) => {
                            if (tenant["leaseTypeCode"] === "1") {
                                tenant["includeInsuranceFlag"] = undefined;
                                tenant["includeManagementFlag"] = undefined;
                                tenant["includeMaintenanceFlag"] = undefined;
                                tenant["includeHydroFlag"] = undefined;
                                tenant["includeWaterFlag"] = undefined;
                                tenant["includeHvacFlag"] = undefined;
                                tenant["includePropertyTaxFlag"] = undefined;
                            } else if (tenant["leaseTypeCode"] === "2") {
                                tenant["includeInsuranceFlag"] = true;
                                tenant["includeManagementFlag"] = true;
                                tenant["includeMaintenanceFlag"] = true;
                                tenant["includeHydroFlag"] = true;
                                tenant["includeWaterFlag"] = true;
                                tenant["includeHvacFlag"] = true;
                                tenant["includePropertyTaxFlag"] = true;
                            }
                            return tenant;
                        });
                        setTenantList(tenantSummary);
                        if (summary.length > 10) {
                            setTotalPages(Math.ceil(summary.length / 10));
                        } else {
                            setTotalPages(undefined);
                        }
                        if (tenantSummary.filter((tenant) => (
                            (["2", "3", "4"].includes(tenant["leaseStatusCode"] as string) && (!tenant["endDate"] || tenant["endDate"] === ""))
                            || ((tenant["leaseStatusCode"] === "1" || tenant["leaseStatusCode"] === "5") && tenant["endDate"])
                        )).length === 0) {
                            if (tenantSummary.filter((tenant) => ((tenant["endDate"] && new Date(tenant["endDate"] as string) >= new Date()) || tenant["leaseStatusCode"] === "1" || tenant["leaseStatusCode"] === "5")).length === 0) {
                                setCurrentWizardPage(2);
                            } else {
                                setCurrentWizardPage(1);
                            }
                        }
                        setAssignedPreviousUnits(true);
                    }
                }
            }
        }
    }, [getFullTenantSummaryState, currentWizardPage, importWizardElements.length, confirmedImport, assignedPreviousUnits, fetchedFullUnits, t, fetchFullUnitsDataHasRun]);

    useEffect(() => {
        if (proceed && !closingWizard) {
            if (wizardUpdated) {
                const keys = Object.keys(wizardNumber);
                const tList: Record<string, unknown>[] = [];
                keys.forEach((key) => {
                    let numbersToPush = 0;
                    if ((wizardNumber[key] as number) >= (oldWizardNumber[key] as number)) {
                        tList.push(...tenantList.filter((tenant) => tenant["floorLevel"] === key));
                        numbersToPush = (wizardNumber[key] as number) - (oldWizardNumber[key] as number);
                    } else {
                        numbersToPush = (wizardNumber[key] as number);
                    }
                    for (let i = 0; i < numbersToPush; i++) {
                        tList.push({
                            ...getDefaultUnit(),
                            floorLevel: key,
                            snapshotId: props.snapshotId,
                            unitTypeCode: "1"
                        });
                    }
                });
                setTenantList(tList);
                if (tList.length > 10) {
                    setTotalPages(Math.ceil(tList.length / 10));
                } else {
                    setTotalPages(undefined);
                }
                setWizardUpdated(false);
            }
            setCurrentPage(1);
            setWarningMessage("");
            setProceed(false);
            const pageElements = confirmedImport === true ? importWizardElements : orderedWizardElements
            if (currentWizardPage < (pageElements.length - 1)) {
                if (pageElements[currentWizardPage + 1].key === "wizard-update-import" && confirmedArr.length === tenantList.length && confirmedArr.length !== 0) {
                    setCurrentWizardPage(importWizardElements.length - 1);
                } else {
                    setCurrentWizardPage(currentWizardPage + 1);
                }
            }
            setShowWarningModal(false);
        }
        // eslint-disable-next-line
    }, [wizardUpdated, proceed]);

    useEffect(() => {
        if (closingWizard && proceed) {
            setClosingWizard(false);
            setProceed(false);
            setWarningMessage("");
            props.onCancel((currentWizardPage + 1), "cancel_button", submitted);
        }
    }, [closingWizard, currentWizardPage, proceed, props, submitted]);

    useEffect(() => {
        if (isSameType && unitUseForAll !== "") {
            tenantList.forEach((tenant) => tenant["unitSubtypeCode"] = unitUseForAll);
            setTenantList([...tenantList]);
        } else if (isSameType === false) {
            setUnitUseForAll("");
        }
        // eslint-disable-next-line
    }, [isSameType, unitUseForAll]);

    useEffect(() => {
        if (leaseTypeSameType && leaseTypeForAll !== "") {
            tenantList.forEach((tenant) => {
                if (tenant["leaseStatusCode"] as string === '1') {
                    tenant["leaseTypeCode"] = undefined;
                } else {
                    if (leaseTypeForAll === "1" || (tenant["leaseTypeCode"] === "2" && (leaseTypeForAll === "3" || leaseTypeForAll === "4"))) {
                        tenant["includeInsuranceFlag"] = undefined;
                        tenant["includeManagementFlag"] = undefined;
                        tenant["includeMaintenanceFlag"] = undefined;
                        tenant["includeHydroFlag"] = undefined;
                        tenant["includeWaterFlag"] = undefined;
                        tenant["includeHvacFlag"] = undefined;
                        tenant["includePropertyTaxFlag"] = undefined;
                    }
                    if (leaseTypeForAll === "2") {
                        tenant["includeInsuranceFlag"] = true;
                        tenant["includeManagementFlag"] = true;
                        tenant["includeMaintenanceFlag"] = true;
                        tenant["includeHydroFlag"] = true;
                        tenant["includeWaterFlag"] = true;
                        tenant["includeHvacFlag"] = true;
                        tenant["includePropertyTaxFlag"] = true;
                    }
                    tenant["leaseTypeCode"] = leaseTypeForAll;
                }
            });
            setTenantList([...tenantList]);
        } else if (leaseTypeSameType === false) {
            setLeaseTypeForAll("");
        }
        // eslint-disable-next-line
    }, [leaseTypeSameType, leaseTypeForAll]);

    useEffect(() => {
        if (!hasFreeMonth) {
            tenantList.forEach((tenant) => tenant["freeRentMonth"] = undefined);
            setTenantList([...tenantList]);
        }
        // eslint-disable-next-line
    }, [hasFreeMonth]);

    useEffect(() => {
        if (!hasLandLease) {
            tenantList.forEach((tenant) => tenant["landLeaseFlag"] = undefined);
            setTenantList([...tenantList]);
        }
        // eslint-disable-next-line
    }, [hasLandLease]);

    useEffect(() => {
        if (!hasStepUp) {
            tenantList.forEach((tenant) => {
                tenant["stepUpDate1"] = undefined
                tenant["stepUpRate1"] = undefined
                tenant["stepUpDate2"] = undefined
                tenant["stepUpRate2"] = undefined
                tenant["stepUpDate3"] = undefined
                tenant["stepUpRate3"] = undefined
                tenant["stepUpDate4"] = undefined
                tenant["stepUpRate4"] = undefined
            });
            setStepUpArr([]);
            setTenantList([...tenantList]);
        }
        // eslint-disable-next-line
    }, [hasStepUp]);

    useEffect(() => {
        if ((isInternalUser && selectDelete) || !isInternalUser) {
            if (confirmedImport === false) {
                if (getFullTenantSummaryState.tenantSummary) {
                    const tenantIds = getFullTenantSummaryState.tenantSummary
                        .filter(t => !deletedUnits.includes(t.tenantId as number))
                        .map(t => t.tenantId as number)
                    if (tenantIds.length) {
                        console.log("GuidedExperienceWizard delete multiple units by effect (isInternalUser && selectDelete) || !isInternalUser confirmedImport === false")
                        dispatch(deleteMultipleIncomeUnitsByIds(props.snapshotId, tenantIds))
                        setDeletedUnits([...deletedUnits, ...tenantIds])
                    }
                    setTenantList([]);
                }
            }
            if (isInternalUser) {
                setSelectDelete(false)
            }
        }
    }, [confirmedImport, deletedUnits, dispatch, getFullTenantSummaryState.tenantSummary, isInternalUser, props.snapshotId, selectDelete]);

    useEffect(() => {
        if (!deleteIncomeUnitState.loading && deleteIncomeUnitState.success) {
            setFetchedFullUnits(false);
        }
    }, [deleteIncomeUnitState, dispatch])

    const addMultipleUnits = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        const wizardEventPayload = createWizardEventPayload({
            wizard_type: "commercial",
            wizard_interaction_type: "submit",
            wizard_units_saved: (tenantList.length | confirmedTenants.length),
            wizard_step: currentWizardPage + 1
        })
        trackWizardEvent(wizardEventPayload)
        if (confirmedImport) {
            if (removedTenantIds.current.length > 0) {
                let tenantIds: number[] = []
                for (const i in removedTenantIds.current) {
                    if (!deletedUnits.includes(removedTenantIds.current[i] as number)) {
                        tenantIds = [...tenantIds, removedTenantIds.current[i] as number]
                    }
                }
                if (tenantIds.length) {
                    console.log("GuidedExperienceWizard delete multiple units from submit")
                    dispatch(deleteMultipleIncomeUnitsByIds(props.snapshotId, tenantIds));
                    setDeletedUnits([...deletedUnits, ...tenantIds])
                }
            }
            const updatedTenantList = [...tenantList, ...confirmedTenants]
            if (updatedTenantList.length > 0){
                dispatch(upsertBatchIncomeUnits(props.snapshotId, {
                    "added": [],
                    "updated": updatedTenantList,
                    "property_code": sessionStorage.getItem("propertyCode"),
                    "profile_type_code": sessionStorage.getItem("profileTypeCode"),
                    "trigger_spectral": false // wizard has no spectral integration
                }))
            } else {
                dispatch(setSaveResultSuccess())
            }
        } else if (confirmedImport === false) {
            dispatch(upsertBatchIncomeUnits(props.snapshotId, {
                "added": [...tenantList, ...confirmedTenants],
                "updated": [],
                "property_code": sessionStorage.getItem("propertyCode"),
                "profile_type_code": sessionStorage.getItem("profileTypeCode"),
                "trigger_spectral": false // wizard has no spectral integration
            }))
        }
        setSubmitted(true);
    }

    const closeWizardModal = () => {
        setShowWarningModal(false);
        setClosingWizard(false);
        setWarningMessage("")
    }

    return (<LoadingSpinner loading={!props.snapshotId || getFullTenantSummaryState.loading || saveResultState.loading || deleteIncomeUnitState.loading}>
        <div className='guided-experience-wizard'>
            <div>
                <p>{confirmedImport !== undefined && (t("step-1") + (currentWizardPage + 1) + t("step-2") + (confirmedImport === true ? importWizardElements.length : orderedWizardElements.length))}</p>
                {confirmedImport === undefined ?
                    <ConfirmImportWizard key="wizard-confirm-import" setConfirmedImport={setConfirmedImport} setSelectDelete={setSelectDelete} confirmedImport={confirmedImport} /> :
                    confirmedImport === true ?
                        (getFullTenantSummaryState.loading ? <LoadingSpinner loading={true} /> : importWizardElements[currentWizardPage]) :
                        orderedWizardElements[currentWizardPage]}
            </div>
            <div className='guided-experience-wizard-buttons'>
                <Button type='cancel' onClick={() => props.onCancel((currentWizardPage + 1), "cancel_button", submitted)} testId='wizard-close-button'>
                    {t("cancel").toLocaleUpperCase(i18n.language)}
                </Button>
                {((confirmedImport === true ? importWizardElements.length > 1 && currentWizardPage > 2 : orderedWizardElements.length > 1 && currentWizardPage !== 0) && !(confirmedArr.length === [...tenantList, ...confirmedTenants].length && confirmedArr.length !== 0)) && <Button type='secondary' onClick={() => previousWizardPage()} testId='wizard-previous-button'>
                    {t("back").toLocaleUpperCase(i18n.language)}
                </Button>}
                {currentWizardPage !== ((confirmedImport === true ? importWizardElements.length : orderedWizardElements.length) - 1) &&
                    (confirmedImport === true ? importWizardElements.length : orderedWizardElements.length) > 1 && <Button type='secondary' onClick={() => nextWizardPage()} testId='wizard-next-button'>
                        {t("next").toLocaleUpperCase(i18n.language)}
                    </Button>}
                {currentWizardPage === ((confirmedImport === true ? importWizardElements.length : orderedWizardElements.length) - 1) && (confirmedImport === true ? importWizardElements.length : orderedWizardElements.length) > 1 &&
                    <Button type='submit' onClick={addMultipleUnits} testId='wizard-submit-button'>
                        {t(confirmedImport === true ? "update-unit-1" : "add-units-1").toLocaleUpperCase(i18n.language) + [...tenantList, ...confirmedTenants].length + t("add-units-2").toLocaleUpperCase(i18n.language)}
                    </Button>
                }
            </div>
        </div>
        {(showWarningModal || closingWizard) &&
            <Modal>
                <div className='guided-wizard-warning-modal'>
                    <p>{warningMessage}</p>
                    <div className='guided-wizard-warning-buttons'>
                        <Button type='secondary' onClick={() => setProceed(true)} className='guided-wizard-warning-cancel' testId='guided-wizard-warning-cancel'>
                            {t("yes").toLocaleUpperCase(i18n.language)}
                        </Button>
                        <Button type='cancel' onClick={() => closeWizardModal()} testId='guided-wizard-warning-no'>
                            {t("no").toLocaleUpperCase(i18n.language)}
                        </Button>
                    </div>
                </div>
            </Modal>
        }
    </LoadingSpinner>)
}
